// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import GameMode from '@app/features/accessibility/state/GameMode';
import GuildWallpapers from '@app/features/accessibility/state/GuildWallpapers';
import {useSaveData} from '@app/features/app/hooks/useSaveData';
import styles from '@app/features/channel/components/GuildChatWallpaper.module.css';
import type {Guild} from '@app/features/guild/models/Guild';
import ThemeLibrary from '@app/features/theme/state/ThemeLibrary';
import {int2hex} from '@app/features/theme/utils/ColorUtils';
import {getGuildWallpaperURL} from '@app/features/user/utils/AvatarUtils';
import {isWeakDevice} from '@app/features/voice_scene/utils/RoomViewAvailability';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useSyncExternalStore} from 'react';

const MORE_CONTRAST_QUERY = '(prefers-contrast: more), (forced-colors: active)';

function subscribeMoreContrast(onChange: () => void): () => void {
	const query = window.matchMedia(MORE_CONTRAST_QUERY);
	query.addEventListener('change', onChange);
	return () => query.removeEventListener('change', onChange);
}

function readMoreContrast(): boolean {
	return window.matchMedia(MORE_CONTRAST_QUERY).matches;
}

export function hasGuildWallpaper(guild: Pick<Guild, 'wallpaper' | 'wallpaperColor'>): boolean {
	return guild.wallpaper !== null || guild.wallpaperColor !== null;
}

export const GuildChatWallpaper = observer(({guild}: {guild: Guild}) => {
	const saveData = useSaveData();
	const moreContrast = useSyncExternalStore(subscribeMoreContrast, readMoreContrast);
	const showImage =
		guild.wallpaper !== null &&
		GuildWallpapers.enabled &&
		!GameMode.lightMode &&
		!saveData &&
		!moreContrast &&
		!ThemeLibrary.activeThemeCss &&
		!Accessibility.customThemeCss?.trim() &&
		!isWeakDevice(navigator);
	const tint = guild.wallpaperColor === null ? undefined : int2hex(guild.wallpaperColor);
	return (
		<div
			className={styles.layer}
			style={tint ? ({'--guild-wallpaper-tint': tint} as React.CSSProperties) : undefined}
			data-tinted={tint ? (showImage ? 'image' : 'color') : undefined}
			aria-hidden="true"
			data-flx="channel.guild-chat-wallpaper"
		>
			{showImage && (
				<img
					className={styles.image}
					src={getGuildWallpaperURL({id: guild.id, wallpaper: guild.wallpaper!})}
					alt=""
					draggable={false}
					decoding="async"
					data-flx="channel.guild-chat-wallpaper.image"
				/>
			)}
		</div>
	);
});
